import React from 'react';

const SupportHoursTimeline = ({ tickets, isLoading, orders, isLoadingOrders, t, isAdmin }) => {
    let events = [];

    // Add tickets
    if (tickets && tickets.length > 0) {
        tickets.filter(ticket => !ticket.doNotInvoice).forEach(ticket => {
            events.push({
                type: 'ticket',
                id: `ticket-${ticket.id}`,
                date: ticket.closedAt ? new Date(ticket.closedAt) : new Date(0),
                data: ticket
            });
        });
    }

    // Add orders
    if (orders && orders.length > 0) {
        orders.forEach(order => {
            (order.cartItems || [])
                .filter(item => item.hours != null)
                .forEach(item => {
                    events.push({
                        type: 'order',
                        id: `order-${order.id}-${item.id}`,
                        date: order.createdAt ? new Date(order.createdAt) : new Date(0),
                        data: {
                            orderId: order.id,
                            name: item.name,
                            hours: item.hours * (item.quantity || 1),
                            orderUserProfilePicture: order.orderUserProfilePicture,
                            orderUser: order.orderUser
                        }
                    });
                });
        });
    }

    // Filter to only include current year's events and events on or after Aug 13, 2026
    const currentYear = new Date().getFullYear();
    const cutoffDate = new Date('2026-08-13');
    events = events.filter(event => event.date.getFullYear() === currentYear && event.date >= cutoffDate);

    // Sort ascending by date to calculate running total
    events.sort((a, b) => a.date - b.date);

    let currentTotal = 0;
    events.forEach(event => {
        if (event.type === 'order') {
            currentTotal += event.data.hours;
        } else if (event.type === 'ticket') {
            const hours = event.data.timeSpentHours || (event.data.timeSpentSeconds ? event.data.timeSpentSeconds / 3600 : 0);
            currentTotal -= hours;
        }
        event.accumulatedHours = currentTotal;
    });

    // Sort descending by date for display
    events.sort((a, b) => b.date - a.date);

    const isLoadingAny = isLoading || isLoadingOrders;

    return (
        <>
            <div className="flex justify-between items-center mb-6 px-12 text-[11px] font-bold uppercase tracking-widest">
                <div className="w-5/12 flex justify-end">
                    <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-4 py-2 rounded-xl shadow-sm">
                        {t?.purchased_support_hours || 'Aangeschafte Supporturen'}
                    </span>
                </div>
                <div className="w-2/12 text-center"></div>
                <div className="w-5/12 flex justify-start">
                    <span className="bg-purple-50 text-purple-700 border border-purple-100 px-4 py-2 rounded-xl shadow-sm">
                        {t?.closed_tickets || 'Gesloten Tickets'}
                    </span>
                </div>
            </div>

            <div className="overflow-y-auto flex-1 px-4 py-2 relative">
                {isLoadingAny ? (
                    <div className="relative wrap py-2">
                        <div className="absolute border-opacity-20 border-gray-400 h-full border-l-2 left-1/2 -ml-[1px] border-dashed"></div>
                        
                        {/* Skeleton Item 1 (Right) */}
                        <div className="mb-6 flex justify-between items-center w-full right-timeline">
                            <div className="order-1 w-5/12"></div>
                            <div className="z-20 flex items-center order-1 bg-gray-200 shadow-sm w-6 h-6 rounded-full border-2 border-white animate-pulse"></div>
                            <div className="order-1 w-5/12 px-4 py-3 bg-gray-50 rounded-lg shadow-sm border border-gray-100 flex items-center justify-start gap-3 animate-pulse">
                                <div className="w-6 h-4 bg-gray-200 rounded shrink-0"></div>
                                <div className="w-16 h-6 bg-purple-100 rounded-full shrink-0"></div>
                                <div className="flex-1 space-y-2 py-1">
                                    <div className="h-3 bg-gray-300 rounded w-1/3"></div>
                                    <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                                </div>
                                <div className="w-8 h-8 bg-gray-200 rounded-full shrink-0 ml-2"></div>
                            </div>
                        </div>

                        {/* Skeleton Item 2 (Left) */}
                        <div className="mb-6 flex justify-between items-center w-full left-timeline">
                            <div className="order-1 w-5/12 px-4 py-3 bg-gray-50 rounded-lg shadow-sm border border-gray-100 flex items-center justify-end gap-3 animate-pulse">
                                <div className="w-8 h-8 bg-gray-200 rounded-full shrink-0"></div>
                                <div className="flex-1 space-y-2 py-1 flex flex-col items-end">
                                    <div className="h-3 bg-gray-300 rounded w-1/3"></div>
                                    <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                                </div>
                                <div className="w-12 h-6 bg-emerald-100 rounded-full shrink-0"></div>
                                <div className="w-6 h-4 bg-gray-200 rounded shrink-0"></div>
                            </div>
                            <div className="z-20 flex items-center order-1 bg-gray-200 shadow-sm w-6 h-6 rounded-full border-2 border-white animate-pulse"></div>
                            <div className="order-1 w-5/12"></div>
                        </div>

                        {/* Skeleton Item 3 (Right) */}
                        <div className="mb-6 flex justify-between items-center w-full right-timeline">
                            <div className="order-1 w-5/12"></div>
                            <div className="z-20 flex items-center order-1 bg-gray-200 shadow-sm w-6 h-6 rounded-full border-2 border-white animate-pulse"></div>
                            <div className="order-1 w-5/12 px-4 py-3 bg-gray-50 rounded-lg shadow-sm border border-gray-100 flex items-center justify-start gap-3 animate-pulse">
                                <div className="w-6 h-4 bg-gray-200 rounded shrink-0"></div>
                                <div className="w-14 h-6 bg-purple-100 rounded-full shrink-0"></div>
                                <div className="flex-1 space-y-2 py-1">
                                    <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                                    <div className="h-2 bg-gray-200 rounded w-5/6"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : events.length > 0 ? (
                    <div className="relative wrap py-2">
                        <div className="absolute border-opacity-20 border-gray-400 h-full border-l-2 left-1/2 -ml-[1px] border-dashed"></div>
                        
                        {events.map((event, index) => {
                            const currentMonth = event.date.toLocaleString('en-US', { month: 'short' });
                            const prevEvent = index > 0 ? events[index - 1] : null;
                            const prevMonth = prevEvent ? prevEvent.date.toLocaleString('en-US', { month: 'short' }) : null;
                            const showMonthHeader = currentMonth !== prevMonth;

                            let eventBlock = null;
                            if (event.type === 'order') {
                                eventBlock = (
                                    <div key={event.id} className="mb-4 flex justify-between items-center w-full right-timeline">
                                        <div className="order-1 w-5/12">
                                            <div className="px-4 py-3 bg-emerald-50 rounded-lg shadow-sm border border-emerald-100 flex items-center justify-end gap-3 hover:shadow-md transition-shadow">
                                                {event.data.orderUserProfilePicture ? (
                                                    <img src={event.data.orderUserProfilePicture} alt={event.data.orderUser || 'User'} className="w-8 h-8 rounded-full border border-gray-200 shadow-sm shrink-0 object-cover" title={event.data.orderUser} referrerPolicy="no-referrer" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 border border-emerald-200" title={event.data.orderUser || 'User'}>
                                                        <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                                                    </div>
                                                )}
                                                <div className="text-right flex flex-col justify-center min-w-0 flex-1">
                                                    <div className="flex items-center justify-end gap-2 w-full">
                                                        <h4 className="font-bold text-sm text-gray-800 truncate" title={event.data.orderId}>
                                                            {isAdmin ? (
                                                                <a href={`/admin/afas-transactions/${event.data.orderId}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                                    {event.data.orderId}
                                                                </a>
                                                            ) : event.data.orderId}
                                                        </h4>
                                                        <span className="text-xs text-emerald-600 font-semibold whitespace-nowrap shrink-0">{event.date.toLocaleDateString()}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 truncate w-full" title={event.data.name}>
                                                        {event.data.name}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <div className="bg-emerald-100 px-3 py-1 rounded-full whitespace-nowrap">
                                                        <span className="text-sm font-black text-emerald-700">+ {event.data.hours}h</span>
                                                    </div>
                                                    <div className="font-bold text-gray-400 w-8 text-center text-sm shrink-0" title="Accumulated Support Hours">
                                                        {event.accumulatedHours.toFixed(1).replace('.0', '')}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="z-20 flex items-center order-1 bg-emerald-500 shadow-sm w-6 h-6 rounded-full border-2 border-white">
                                            <svg className="mx-auto w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                        </div>
                                        <div className="order-1 w-5/12"></div>
                                    </div>
                                );
                            } else {
                                eventBlock = (
                                    <div key={event.id} className="mb-4 flex justify-between items-center w-full left-timeline">
                                        <div className="order-1 w-5/12"></div>
                                        <div className="z-20 flex items-center order-1 bg-purple-500 shadow-sm w-6 h-6 rounded-full border-2 border-white">
                                            <svg className="mx-auto w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                                            </svg>
                                        </div>
                                        <div className="order-1 w-5/12">
                                            <div className="px-4 py-3 bg-purple-50 rounded-lg shadow-sm border border-purple-100 flex items-center justify-start gap-3 hover:shadow-md transition-shadow">
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <div className="font-bold text-gray-400 w-8 text-center text-sm shrink-0" title="Accumulated Support Hours">
                                                        {event.accumulatedHours.toFixed(1).replace('.0', '')}
                                                    </div>
                                                    <div className="bg-purple-100 px-3 py-1 rounded-full whitespace-nowrap">
                                                        <span className="text-sm font-black text-purple-700">
                                                            - {(() => {
                                                                const hours = event.data.timeSpentHours || (event.data.timeSpentSeconds ? event.data.timeSpentSeconds / 3600 : 0);
                                                                if (hours < 1 && hours > 0) {
                                                                    const minutes = Math.round(hours * 60);
                                                                    return `${minutes}m`;
                                                                }
                                                                return `${hours.toFixed(2).replace(/\.00$/, '')}h`;
                                                            })()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-left flex flex-col justify-center min-w-0 flex-1">
                                                    <div className="flex items-center justify-start gap-2 w-full">
                                                        <h4 className="font-bold text-sm text-gray-800 whitespace-nowrap shrink-0" title={event.data.issueKey}>
                                                            {event.data.issueKey}
                                                        </h4>
                                                        <span className="text-xs text-purple-600 font-semibold whitespace-nowrap shrink-0">{event.date.toLocaleDateString()}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-600 truncate w-full" title={event.data.summary}>{event.data.summary}</p>
                                                </div>
                                                {event.data.loggedUserPictures && Object.keys(event.data.loggedUserPictures).length > 0 && (
                                                    <div className="flex items-center -space-x-2 shrink-0 ml-2">
                                                        {Object.entries(event.data.loggedUserPictures).map(([email, pic], idx) => (
                                                            pic ? (
                                                                <img key={idx} src={pic} alt={email} className="w-8 h-8 rounded-full border-2 border-white shadow-sm relative z-10 hover:z-20 transition-all object-cover" title={email} referrerPolicy="no-referrer" />
                                                            ) : (
                                                                <div key={idx} className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center border-2 border-white shadow-sm relative z-10 hover:z-20 transition-all" title={email}>
                                                                    <span className="text-[10px] font-bold text-purple-700">{email.charAt(0).toUpperCase()}</span>
                                                                </div>
                                                            )
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <React.Fragment key={`event-wrapper-${event.id}`}>
                                    {showMonthHeader && (
                                        <div className="flex justify-center w-full mb-4 relative z-20">
                                            <span className="bg-white border-2 border-gray-200 text-gray-500 font-bold text-xs px-4 py-1 rounded-full shadow-sm tracking-widest">
                                                {currentMonth}
                                            </span>
                                        </div>
                                    )}
                                    {eventBlock}
                                </React.Fragment>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-20 text-gray-500 flex flex-col items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-lg">{t?.no_history_found || 'Geen geschiedenis gevonden.'}</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default SupportHoursTimeline;
